import { useState } from 'react';
import { useOne } from '@refinedev/core';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, TableOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { DetailField, DetailPage, space, useConfirmAction, useUrlTabState } from '@viq-admin/ui';
import { LEAD_RULES_COPY } from '@viq-admin/screens';
import type { Company } from '@viq-admin/types';
import { archiveCompany, restoreCompany } from '../../api/companies';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { LeadsMiniTable } from '../../components/LeadsMiniTable';
import { useTrackRecent } from '../../recent/recentItems';
import { CompanyEdit } from './edit';
import { CompanyPromptsSection } from './CompanyPromptsSection';
import { CompanyLeadRulesTab } from './CompanyLeadRulesTab';
import { COMPANY_CRM_FLAGS, COMPANY_FLAG_HELP } from './companyFlagHelp';
import { CompanyFeatureTags } from './hub/CompanyFeatureTags';
import { CompanyOverviewTab } from './hub/CompanyOverviewTab';
import { CompanyUsersTab } from './hub/CompanyUsersTab';
import { CompanyConferencesTab } from './hub/CompanyConferencesTab';
import { CompanyDevicesTab } from './hub/CompanyDevicesTab';
import { CompanyIntegrationsTab } from './hub/CompanyIntegrationsTab';
import { useCompanyConferences, useCompanyUsers } from './hub/useCompanyHubData';
import { Can } from '../../ability/AbilityContext';

/**
 * Tab keys, in display order. `overview`, `flags`, `prompts` and
 * `lead-rules` predate the hub tabs and keep their keys so existing
 * `?tab=` links still land on the same tab.
 */
const TAB_KEYS = [
  'overview',
  'users',
  'conferences',
  'leads',
  'devices',
  'integrations',
  'flags',
  'prompts',
  'lead-rules',
];

/** Leads tab: more rows than the Overview's "Recent leads", still one request. */
const LEADS_TAB_LIMIT = 50;

/**
 * Company detail — the hub for one tenant: who its users are, which events it
 * has, its CRM state, its leads and its devices, each on a tab, with an
 * Overview of summary cards on top. A tabbed shell on the shared DetailPage,
 * URL-synced via `useUrlTabState` (same pattern as ConferenceShow). One
 * route, /companies/:id; the same route serves archived records (Restore
 * instead of Archive).
 *
 * Every read on this page names this company explicitly (see
 * hub/useCompanyHubData.ts), never the global "Operate as" scope, so the page
 * shows the same data whatever the picker says. The two header shortcuts are
 * the deliberate exception (plan decision 3): "Open in Batch" and "Live feed"
 * SET the global scope to this company, then open those screens, which only
 * follow the global scope. They are hidden for an archived company.
 *
 * Tab bodies mount when first opened (antd Tabs' default), so each hub tab
 * fetches only once shown. The Users and Conferences queries are the
 * exception: the tab labels carry their counts, so they load with the page.
 *
 * The page-level Edit button still flips the WHOLE page to `CompanyEdit`
 * (name, Entra hint, every feature flag) in place — that edit is unrelated to
 * tab selection, and tabs are unmounted while editing (see DetailPage's
 * whole-page-fold doc comment). Lead rules is the exception: it has its own
 * Save button inside its tab and saves independently of the page-level edit.
 *
 * This console's dataProvider throws an HttpError with a statusCode on any
 * non-2xx response, so a missing record surfaces through useOne as
 * `query.error` with `statusCode === 404` — never as an empty success. The
 * generic `error` prop is suppressed when `notFound` is true so DetailPage
 * doesn't render both an error panel and the 404 Result.
 */
export function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const confirm = useConfirmAction();
  const { setCompanyId } = useCompanyScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, onTabChange] = useUrlTabState(searchParams, setSearchParams, {
    keys: TAB_KEYS,
    defaultKey: 'overview',
  });

  const { result: company, query } = useOne<Company>({
    resource: 'companies',
    id,
    queryOptions: { enabled: !!id },
  });

  // Counts for the tab labels. Same queries (same keys) the Overview cards
  // and the Users / Conferences tabs read, so this adds no requests.
  const usersQuery = useCompanyUsers(company ? id : undefined);
  const conferencesQuery = useCompanyConferences(company ? id : undefined);

  useTrackRecent(company && id ? { kind: 'company', id, label: company.name } : null);

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;
  const isArchived = company?.archivedAt != null;

  /** Plan decision 3: these shortcuts deliberately set the global scope. */
  function openScoped(href: string) {
    setCompanyId(id!);
    navigate(href);
  }

  function handleArchive() {
    confirm({
      title: 'Archive company',
      content: 'This company will no longer be able to access the platform.',
      okText: 'Archive',
      danger: true,
      successMessage: 'Company archived.',
      onConfirm: async () => {
        await archiveCompany(id!);
        navigate('/companies/archived');
      },
    });
  }

  function handleRestore() {
    confirm({
      title: 'Restore company?',
      content: 'This company will regain access to the platform.',
      okText: 'Restore',
      successMessage: 'Company restored.',
      onConfirm: async () => {
        await restoreCompany(id!);
        navigate('/companies');
      },
    });
  }

  const archiveAction = (
    <Can I="manage" a="Company" passThrough>
      {(allowed: boolean) =>
        allowed ? (
          <Button danger={!isArchived} onClick={isArchived ? handleRestore : handleArchive}>
            {isArchived ? 'Restore' : 'Archive'}
          </Button>
        ) : null
      }
    </Can>
  );

  // PageHeader lays these out in a Space that wraps on a phone.
  const actions = (
    <>
      {!isArchived && (
        <>
          <Button icon={<TableOutlined />} onClick={() => openScoped('/batch')}>
            Open in Batch
          </Button>
          <Button icon={<ThunderboltOutlined />} onClick={() => openScoped('/live-feed')}>
            Live feed
          </Button>
        </>
      )}
      {archiveAction}
    </>
  );

  const flagValue = (on: boolean, help: string, label: string) => (
    <Space>
      {on ? <Tag color="green">On</Tag> : <Tag>Off</Tag>}
      <Tooltip title={help}>
        <QuestionCircleOutlined aria-label={`About ${label}`} />
      </Tooltip>
    </Space>
  );

  const withCount = (label: string, q: { loaded: boolean }, n: number) =>
    q.loaded ? `${label} (${n})` : label;

  const tabs = company
    ? [
        {
          key: 'overview',
          label: 'Overview',
          children: <CompanyOverviewTab company={company} onOpenTab={onTabChange} />,
        },
        {
          key: 'users',
          label: withCount('Users', usersQuery, usersQuery.users.length),
          children: <CompanyUsersTab companyId={company.id} />,
        },
        {
          key: 'conferences',
          label: withCount('Conferences', conferencesQuery, conferencesQuery.conferences.length),
          children: <CompanyConferencesTab companyId={company.id} />,
        },
        {
          key: 'leads',
          label: 'Leads',
          children: (
            <LeadsMiniTable filter={{ companyId: company.id }} hide={['company']} limit={LEADS_TAB_LIMIT} />
          ),
        },
        { key: 'devices', label: 'Devices', children: <CompanyDevicesTab companyId={company.id} /> },
        {
          key: 'integrations',
          label: 'Integrations',
          children: <CompanyIntegrationsTab company={company} />,
        },
        {
          key: 'flags',
          label: 'Feature flags',
          children: (
            <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="company-flags-view">
              <DetailField
                label="Store card images"
                value={flagValue(company.storeCardImages, COMPANY_FLAG_HELP.storeCardImages, 'store card images')}
              />
              <DetailField
                label="Move leads between conferences"
                value={flagValue(
                  company.leadMoveEnabled,
                  COMPANY_FLAG_HELP.leadMoveEnabled,
                  'move leads between conferences',
                )}
              />
              <DetailField
                label="Auto enrichment"
                value={flagValue(
                  company.autoEnrichApollo,
                  COMPANY_FLAG_HELP.autoEnrichApollo,
                  'auto enrichment',
                )}
              />
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Typography.Text strong type="secondary">CRM integrations</Typography.Text>
                {COMPANY_CRM_FLAGS.map(({ name, label }) => (
                  <DetailField
                    key={name}
                    label={label}
                    value={flagValue(company[name], COMPANY_FLAG_HELP[name], `${label} integration`)}
                  />
                ))}
              </Space>
            </Space>
          ),
        },
        { key: 'prompts', label: 'AI prompts', children: <CompanyPromptsSection companyId={id!} /> },
        { key: 'lead-rules', label: LEAD_RULES_COPY.title, children: <CompanyLeadRulesTab companyId={id!} /> },
      ]
    : undefined;

  return (
    <DetailPage
      title={
        company ? (
          <>
            {company.name}
            {isArchived && (
              <Tag style={{ marginInlineStart: space.sm, verticalAlign: 'middle' }}>Archived</Tag>
            )}
          </>
        ) : (
          'Company'
        )
      }
      subtitle={company ? <CompanyFeatureTags company={company} emptyText="No optional features on" /> : undefined}
      breadcrumbs={[
        { label: 'Companies', href: '/companies' },
        { label: company?.name ?? '' },
      ]}
      onNavigate={({ href }) => navigate(href)}
      onBack={() => navigate('/companies')}
      loading={query?.isLoading}
      error={notFound ? undefined : query?.error}
      notFound={notFound}
      notFoundHref="/companies"
      editing={editing}
      onEditingChange={(next) => {
        setEditing(next);
        // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
        // `dirty` — not just the `onSaved` success path below. Otherwise a
        // dirty-then-discarded session leaves `dirty` stuck true, and the
        // NEXT edit session (freshly remounted, genuinely pristine) still
        // trips the discard guard on its first Cancel.
        if (!next) setDirty(false);
      }}
      editCap="manage:Company"
      dirty={dirty}
      actions={actions}
      readView={null}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={onTabChange}
      editView={
        <CompanyEdit
          id={id}
          onDirtyChange={setDirty}
          onSaved={() => {
            setDirty(false);
            setEditing(false);
          }}
        />
      }
    />
  );
}
